import { useState } from "react";
import { api } from "@/lib/api";

const WORD_LIMIT = 50;

const QUESTIONS = [
  { key: "full_name", label: "What's your full name?", type: "input", placeholder: "" },
  { key: "occupation", label: "What do you do?", type: "textarea", placeholder: "" },
  { key: "socials", label: "What are your social handles?", type: "input", placeholder: "@handle" },
  { key: "working_on", label: "What are you currently working on that excites you most?", type: "textarea", placeholder: "" },
  { key: "city", label: "What city are you based in?", type: "input", placeholder: "e.g. Venice, CA" },
  { key: "email", label: "What's your email?", type: "input", inputType: "email", placeholder: "you@domain.com" },
];

function countWords(s) {
  const trimmed = s.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

export default function ApplicationForm({ onSubmitted, onError }) {
  const [values, setValues] = useState(Object.fromEntries(QUESTIONS.map((q) => [q.key, ""])));
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => {
    if (countWords(v) > WORD_LIMIT) return; // block additional words beyond limit
    setValues((prev) => ({ ...prev, [k]: v }));
  };

  const canSubmit = QUESTIONS.every((q) => values[q.key].trim().length > 0) && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/applications", values);
      setValues(Object.fromEntries(QUESTIONS.map((q) => [q.key, ""])));
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      onError?.(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="application-form" className="space-y-10">
      {QUESTIONS.map((q, idx) => {
        const wc = countWords(values[q.key]);
        const near = wc >= WORD_LIMIT - 5;
        const at = wc >= WORD_LIMIT;
        return (
          <div key={q.key} className="fade-up" style={{ animationDelay: `${idx * 60}ms` }}>
            <label
              className="block small-caps mb-3"
              style={{ color: "#B9929D" }}
              htmlFor={`q-${q.key}`}
            >
              <span style={{ color: "#DDBFCF", marginRight: 8 }}>
                {String(idx + 1).padStart(2, "0")}
              </span>
              {q.label}
            </label>
            {q.type === "textarea" ? (
              <textarea
                id={`q-${q.key}`}
                data-testid={`field-${q.key}`}
                className="kynn-textarea"
                placeholder={q.placeholder}
                value={values[q.key]}
                onChange={(e) => setField(q.key, e.target.value)}
                rows={3}
              />
            ) : (
              <input
                id={`q-${q.key}`}
                data-testid={`field-${q.key}`}
                type={q.inputType || "text"}
                className="kynn-input"
                placeholder={q.placeholder}
                value={values[q.key]}
                onChange={(e) => setField(q.key, e.target.value)}
              />
            )}
            <div
              className="mt-2 text-xs font-mono-body text-right"
              data-testid={`wordcount-${q.key}`}
              style={{
                color: at ? "#b8c975" : near ? "#DDBFCF" : "rgba(226,211,226,0.45)",
                letterSpacing: "0.02em",
              }}
            >
              {wc} / {WORD_LIMIT} words
            </div>
          </div>
        );
      })}

      <div className="pt-6 flex items-center gap-6 flex-wrap">
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="submit-application-btn"
          className="btn-primary"
        >
          {submitting ? "Sending…" : "Send application"} →
        </button>
        <span className="text-xs italic" style={{ color: "#b8c975", lineHeight: 1.5 }}>
          We&rsquo;ll reach out with a confirmation if you&rsquo;re selected ♡
        </span>
      </div>
    </form>
  );
}
